/* eslint-disable no-empty-pattern */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';

import authorizationServices from 'services/authorizationServices';
import { checkEnableParseJSON } from 'components/helper/helper';
import { AxiosError } from 'axios';
import { setAccessToken, setUserId } from 'utils/sessionStorage';
import { toastSuccessIfMessage } from 'utils/toastFromResponse';
// import toast from 'react-hot-toast';
export interface AuthorizationState {
  myAuthorizationEmail: string | undefined;
  verifyCode: string;
  studentId: number | undefined;
  status: 'idle' | 'loading' | 'failed';
  studentAdditionalInfo: any;
}

const initialState: AuthorizationState = {
  myAuthorizationEmail: undefined,
  studentId: 0,
  verifyCode: '',
  status: 'idle',
  studentAdditionalInfo: undefined,
};
export const createStudent = createAsyncThunk(
  'authorization/create-student',
  async (payload: any, {rejectWithValue}) => {
    try {
      const response = await authorizationServices.createStudent(payload);
      console.log('response:', response);
      if (response && response?.response && response?.response?.status !== 200) {
        if (response.response.status !== 400 &&
            response.response?.data?.message) {
          toast.error(response.response.data.message);
        }
      } else if (response && response.message) {
        toast.success(response.message);
      }
      return response;
    }
    catch (err:any) {
      console.log('err:', err);
      const error = err as AxiosError;

      if (error.response?.data) {
        const payloadData: any = error.response.data;
        const errorMessage =
          typeof payloadData === 'string'
            ? payloadData
            : payloadData?.message || payloadData?.response?.data?.message;
        if (errorMessage) {
          toast.error(errorMessage);
        }
        return rejectWithValue(error.response.data);
      }

      const fallback = error.message || 'An unknown error occurred or network issue';
      toast.error(fallback);
      return rejectWithValue({
        message: fallback,
      });
    }
  }
);
export const sendVerificationCode = createAsyncThunk(
  'authorization/sentVerifyCode',
  async (payload: any, {rejectWithValue}) => {
    try {
      const response = await authorizationServices.sendVerificationCode(payload);
      toastSuccessIfMessage(response);
      return response;
    }
    catch (err) {
      const error = err as AxiosError; 
      if (!error.response) {
        throw err;
      }
      return rejectWithValue(error.response.data);
    }
  }
);
export const checkVerificationCode = createAsyncThunk(
  'authorization/checkVerifyCode',
  async (payload: any, {rejectWithValue}) => {
    try {
      const response = await authorizationServices.checkVerificationCode(payload);
      toastSuccessIfMessage(response);
      return response;
    }
    catch (err) {
      const error = err as AxiosError; 
      if (!error.response) {
        throw err;
      }
      return rejectWithValue(error.response.data);
    }
  }
);
export const login = createAsyncThunk('authorization/login', async (payload: any, {rejectWithValue}) => {
  try {
    const response = await authorizationServices.login(payload);
    const hasToken =
      response?.access_token &&
      response?.statusCode !== 430 &&
      response?.code !== 'SESSION_EXISTS';
    if (hasToken) {
      toastSuccessIfMessage(response);
    }
    return response;
  }
  catch (err) {
    const error = err as AxiosError; 
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

export const googleLogin = createAsyncThunk('authorization/googleLogin', async (payload: any, {rejectWithValue}) => {
  try {
    const response = await authorizationServices.googleLogin(payload);
    toastSuccessIfMessage(response);
    return response;
  }
  catch (err) {
    toast.error('Google login failed. Please try again.');
    const error = err as AxiosError; 
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

export const googlerRegister = createAsyncThunk('authorization/googleRegister', async (payload: any, {rejectWithValue}) => {
  try {
    const response = await authorizationServices.googleRegister(payload);
    toastSuccessIfMessage(response);
    return response;
  }
  catch (err) {
    toast.error('Google Register failed. Please try again.');
    const error = err as AxiosError; 
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

export const getStudent = createAsyncThunk('authorization/getStudent', async (payload: any, {rejectWithValue}) => {
  try {
    const response = await authorizationServices.getStudent(payload);
    return response;
  }
  catch (err) {
    const error = err as AxiosError; 
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

export const sendEmailForgotPassword = createAsyncThunk(
  'authorization/forgotPassword',
  async (payload: any, {rejectWithValue}) => {
    try {
      const response = await authorizationServices.sendEmailForgotPassword(payload);
      toastSuccessIfMessage(response);
      return response;
    }
    catch (err) {
      const error = err as AxiosError; 
      if (!error.response) {
        throw err;
      }
      return rejectWithValue(error.response.data);
    }
  }
);
export const checkCodeForgotPassword = createAsyncThunk(
  'authorization/checkForgotPassword',
  async (payload: any, {rejectWithValue}) => {
    try {
      const response = await authorizationServices.checkCodeForgotPassword(payload);
      toastSuccessIfMessage(response);
      return response;
    }
    catch (err) {
      const error = err as AxiosError; 
      if (!error.response) {
        throw err;
      }
      return rejectWithValue(error.response.data);
    }
  }
);
export const resetPassword = createAsyncThunk(
  'authorization/resetPassword',
  async (payload: any, {rejectWithValue}) => {
    try {
      const response = await authorizationServices.resetPassword(payload);
      toastSuccessIfMessage(response);
      return response;
    }
    catch (err) {
      const error = err as AxiosError; 
      if (!error.response) {
        throw err;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const authorizationSlice = createSlice({
  name: 'authorization',
  initialState,
  reducers: {
    setMyAuthorizationEmail: (state, action: any) => {
      state.myAuthorizationEmail = action.payload;
    },
    setVerifyCodeRedux: (state, action: any) => {
      state.verifyCode = action.payload;
    },
    setStudentId: (state, action: any) => {
      state.studentId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStudent.fulfilled, (state, action) => {
        state.studentAdditionalInfo = checkEnableParseJSON(action.payload?.additional_info);
      })
      // Create Student
      .addCase(createStudent.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createStudent.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(createStudent.fulfilled, (state, action) => {
        if (
          !(action.payload && action.payload?.response && action.payload?.response?.status !== 200) &&
          action.payload &&
          action.payload.message
        ) {
          state.studentId = action.payload.student_id;
        }
        state.status = 'idle';
      })

      // Send Verification Code
      .addCase(sendVerificationCode.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(sendVerificationCode.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(sendVerificationCode.fulfilled, (state) => {
        state.status = 'idle';
      })
      // Check Verification Code
      .addCase(checkVerificationCode.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(checkVerificationCode.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(checkVerificationCode.fulfilled, (state) => {
        state.status = 'idle';
      })

      // Login

      .addCase(login.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(login.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(login.fulfilled, (state, action) => {
        const payload = action.payload;
        const hasToken =
          payload?.access_token &&
          payload?.statusCode !== 430 &&
          payload?.code !== 'SESSION_EXISTS';
        if (!hasToken) {
          state.status = 'idle';
          return;
        }
        state.myAuthorizationEmail = payload.email;
        setAccessToken(payload.access_token);
        setUserId(payload.student_id);
        if (payload.student_id) {
          sessionStorage.setItem('session_student_id', String(payload.student_id));
        }
        state.status = 'idle';
      })

       // Google Login
      .addCase(googleLogin.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(googleLogin.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(googleLogin.fulfilled, (state, action) => {
        const payload = action.payload;
        state.status = 'idle';
        if (!payload?.access_token || payload?.should_register) {
          return;
        }
        state.myAuthorizationEmail = payload.email;
        setAccessToken(payload.access_token);
        setUserId(payload.student_id);
        if (payload.student_id) {
          sessionStorage.setItem('session_student_id', String(payload.student_id));
        }

        // if (action.payload) {
        //   state.status = 'idle';
        //   if (action.payload.access_token) {
        //     setAccessToken(action.payload.access_token);
        //   }
        //   if (action.payload.refresh_token) {
        //     sessionStorage.setItem('refresh_token', action.payload.refresh_token);
        //   }
        //   if (action.payload.student_id) {
        //     setUserId(action.payload.student_id);
        //   }
        // }
      })


       // Google Register
       .addCase(googlerRegister.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(googlerRegister.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(googlerRegister.fulfilled, (state, action) => {
        const payload = action.payload;
        state.status = 'idle';
        if (!payload?.access_token || payload?.should_register) {
          return;
        }
        state.myAuthorizationEmail = payload.email;
        setAccessToken(payload.access_token);
        setUserId(payload.student_id);
        if (payload.student_id) {
          sessionStorage.setItem('session_student_id', String(payload.student_id));
        }

        // if (action.payload) {
        //   state.status = 'idle';
        //   if (action.payload.access_token) {
        //     setAccessToken(action.payload.access_token);
        //   }
        //   if (action.payload.refresh_token) {
        //     sessionStorage.setItem('refresh_token', action.payload.refresh_token);
        //   }
        //   if (action.payload.student_id) {
        //     setUserId(action.payload.student_id);
        //   }
        // }
      })



      // Forgot password
      .addCase(sendEmailForgotPassword.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(sendEmailForgotPassword.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(sendEmailForgotPassword.fulfilled, (state) => {
        state.status = 'idle';
      })

      // Check code forgot password
      .addCase(checkCodeForgotPassword.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(checkCodeForgotPassword.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(checkCodeForgotPassword.fulfilled, (state) => {
        state.status = 'idle';
      })
      // Reset password
      .addCase(resetPassword.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(resetPassword.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.status = 'idle';
      });
  },
});

export const { setMyAuthorizationEmail, setVerifyCodeRedux, setStudentId } =
  authorizationSlice.actions;
